import { A } from "@solidjs/router";
import { Logo } from "@storytell/core";
import { TbArrowRight, TbMenu2, TbX } from "solid-icons/tb";
import { createSignal, onMount, Show } from "solid-js";
import { DownloadPageLink, GetStartedButton } from "~/components/marketing/CTAButtons";
import { FEATURE_MENU, MegaMenu, MobileMenuSections, NAV_LINK, TEAM_MENU } from "~/components/marketing/MarketingMenus";
import { useI18n } from "~/i18n";
import { hasFirebaseSession } from "~/lib/firebaseSessionProbe";

export function MarketingHeader() {
  const { t } = useI18n();
  // Guest-first: the server can't see the Firebase session, and most visitors don't have one.
  const [isAuthed, setIsAuthed] = createSignal(false);
  const [menuOpen, setMenuOpen] = createSignal(false);

  onMount(() => {
    void hasFirebaseSession().then(setIsAuthed);
  });

  return (
    <header class="fixed inset-x-0 top-0 z-50 border-b border-primary-border bg-canvas/85 backdrop-blur-md">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="flex h-16 items-center justify-between gap-6">
          <A
            href="/"
            class="inline-flex shrink-0 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
            aria-label={t("marketing.nav.home")}
          >
            <Logo class="h-5 w-auto" markClass="text-accent" textClass="text-[#000D50] dark:text-primary-text" />
          </A>

          <nav class="hidden flex-1 items-center gap-7 md:flex">
            <MegaMenu
              label={t("marketing.nav.features")}
              sections={FEATURE_MENU}
              more={{ href: "/features", label: "marketing.nav.see_all_features" }}
            />
            <MegaMenu label={t("marketing.nav.for_teams")} sections={TEAM_MENU} />
            <A href="/pricing" class={NAV_LINK}>
              {t("marketing.nav.pricing")}
            </A>
            <A href="/blog" class={NAV_LINK}>
              {t("marketing.nav.blog")}
            </A>
          </nav>

          <div class="hidden shrink-0 items-center gap-4 md:flex">
            <AuthActions authed={isAuthed()} size="sm" />
            <DownloadPageLink size="sm" />
          </div>

          <button
            type="button"
            class="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg text-primary-text hover:bg-panel md:hidden"
            aria-expanded={menuOpen()}
            aria-controls="marketing-mobile-menu"
            aria-label={menuOpen() ? t("marketing.nav.close_menu") : t("marketing.nav.open_menu")}
            onClick={() => setMenuOpen(!menuOpen())}
          >
            <Show when={menuOpen()} fallback={<TbMenu2 class="size-5" aria-hidden="true" />}>
              <TbX class="size-5" aria-hidden="true" />
            </Show>
          </button>
        </div>
      </div>

      <Show when={menuOpen()}>
        {/* Any link inside closes the menu; the route change alone wouldn't. */}
        <nav
          id="marketing-mobile-menu"
          class="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-primary-border bg-canvas px-4 pt-4 pb-6 md:hidden"
          onClick={(e) => {
            if (e.target instanceof Element && e.target.closest("a")) setMenuOpen(false);
          }}
        >
          <div class="grid grid-cols-2 gap-4">
            <MobileMenuSections sections={FEATURE_MENU} />
            <MobileMenuSections sections={TEAM_MENU} />
          </div>
          <A href="/features" class="mt-3 inline-flex items-center gap-1.5 py-2 text-sm font-medium text-primary-text">
            {t("marketing.nav.see_all_features")}
            <TbArrowRight class="size-4" aria-hidden="true" />
          </A>
          <ul class="mt-1 flex flex-col border-t border-primary-border pt-2">
            <MobileLink href="/pricing" label={t("marketing.nav.pricing")} />
            <MobileLink href="/blog" label={t("marketing.nav.blog")} />
          </ul>
          <div class="mt-4 flex flex-col items-center gap-3">
            <DownloadPageLink size="md" />
            <AuthActions authed={isAuthed()} size="md" />
          </div>
        </nav>
      </Show>
    </header>
  );
}

/** Sign in plus Get started for a guest, Open Chief for a signed-in visitor. */
const AuthActions = (props: { authed: boolean; size: "sm" | "md" }) => {
  const { t } = useI18n();
  return (
    <Show
      when={props.authed}
      fallback={
        <>
          <A href="/auth/signin" class={NAV_LINK}>
            {t("marketing.nav.sign_in")}
          </A>
          <GetStartedButton variant="secondary" size={props.size} />
        </>
      }
    >
      <A
        href="/app"
        class="inline-flex items-center justify-center rounded-lg border border-primary-border text-sm font-semibold text-primary-text transition-colors hover:bg-panel"
        classList={{ "px-3.5 py-1.5": props.size === "sm", "w-full px-4 py-2.5": props.size === "md" }}
      >
        {t("marketing.nav.open_chief")}
      </A>
    </Show>
  );
};

const MobileLink = (props: { href: string; label: string }) => (
  <li>
    <A href={props.href} class="block py-2.5 text-base font-medium text-primary-text">
      {props.label}
    </A>
  </li>
);
